// Shared feedback for identity frames, roster departures and reconnect/wake reconciliation.
// Only the destination scene's own ready signal completes loading, not the caster read.
import { encounterField } from '../shared/array-types';

type Follow = { casterId: string; destination: string; element: HTMLElement; text: HTMLElement;
    control: HTMLButtonElement; timer?: ReturnType<typeof setTimeout>; retry: () => void; state: string };
const follows = new Map<string, Follow>();
const destinationNames = new Map<string, string>();
let mounted = false;

export function playerFollowOwnedByParent(): boolean {
    return window.parent !== window && window.frameElement?.matches('[data-ft3d-encounter-frame]') === true;
}

export function playerFollowReason(reason: string, casterId = '', encounterId = ''): void {
    if (typeof window.dispatchEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('ft:player-follow-refused', { detail: { reason, actor_id: casterId, encounter_id: encounterId } }));
}
function state(follow: Follow, value: string, text: string, control = ''): void {
    follow.state = value;
    follow.element.dataset.state = value;
    follow.text.textContent = text;
    follow.control.hidden = !control;
    follow.control.textContent = control;
    follow.element.hidden = value === 'ready';
    window.dispatchEvent(new CustomEvent('ft:player-follow-state', { detail: { state: value, casterId: follow.casterId, destination: follow.destination } }));
}
function destinationName(id: string): string {
    return String(encounterField(window.FateTide?.encounterCache?.get(id), 'encounter_name') ?? destinationNames.get(id.toUpperCase()) ?? '').trim();
}
export function rememberPlayerFollowDestination(id: string, name: unknown): void {
    if (typeof name === 'string' && name.trim()) destinationNames.set(id.toUpperCase(), name.trim());
}
function loadingText(id: string): string {
    const name = destinationName(id);
    return `The GM moved your character${name ? ` to ${name}` : ''}. Loading your scene…`;
}
function completeReady(): void {
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    const scene = frame?.contentWindow as (Window & { __ftZoneGenerationPreview?: { sceneReady?: boolean }; ftModifyEncounterPageEncounterArr?: unknown }) | null;
    if (!scene?.__ftZoneGenerationPreview?.sceneReady || scene.document?.body?.classList.contains('ft3d-scene-loading')) return;
    const sceneId = String(encounterField(scene.ftModifyEncounterPageEncounterArr, 'encounter_id') ?? '').toUpperCase();
    const active = String(window.ftGetActiveEncounterId?.() ?? '').toUpperCase();
    for (const follow of follows.values()) {
        if (follow.state !== 'loading' || follow.destination.toUpperCase() !== active || sceneId !== active) continue;
        clearTimeout(follow.timer);
        state(follow, 'ready', 'Your new scene is ready.');
    }
}
export function beginPlayerFollow(casterId: string, destination: string, retry: () => void): Follow {
    if (!mounted) {
        mounted = true;
        window.addEventListener('message', event => {
            const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
            if (event.origin !== window.location.origin || event.source !== frame?.contentWindow) return;
            if (event.data?.type === 'ft3d-scene-ready'
                || event.data?.type === 'ft3d-scene-loading' && event.data.payload?.loading === false) completeReady();
            if (event.data?.type === 'ft3d-scene-loading' && event.data.payload?.failed === true) {
                for (const pending of follows.values()) if (pending.state === 'loading') failPlayerFollow(pending, 'player-follow:scene-failed');
            }
        });
        document.addEventListener('load', completeReady, true);
        window.addEventListener('ft:encounter-state-received', () => {
            for (const pending of follows.values()) if (pending.state === 'loading') pending.text.textContent = loadingText(pending.destination);
        });
    }
    let follow = follows.get(casterId);
    if (!follow) {
        const element = document.createElement('aside');
        element.dataset.ftPlayerFollow = casterId;
        element.className = 'alert alert-info';
        element.setAttribute('role', 'status');
        element.setAttribute('aria-live', 'polite');
        // Feedback must remain over the fullscreen scene and survive iframe replacement.
        Object.assign(element.style, { position: 'fixed', top: '1rem', left: '50%', transform: 'translateX(-50%)', zIndex: '2147483000', maxWidth: '90vw' });
        const text = document.createElement('span'), control = document.createElement('button');
        control.type = 'button'; control.className = 'btn btn-sm btn-light ms-2';
        follow = { casterId, destination, element, text, control, retry, state: '' };
        const current = follow;
        control.addEventListener('click', () => current.retry());
        element.append(text, control);
        document.body.append(element);
        follows.set(casterId, follow);
    }
    clearTimeout(follow.timer);
    follow.destination = destination; follow.retry = retry;
    state(follow, 'reading', loadingText(destination));
    return follow;
}
export function playerFollowLoading(follow: Follow, destination: string, checkReady = true): void {
    clearTimeout(follow.timer);
    follow.destination = destination;
    state(follow, 'loading', loadingText(destination));
    follow.timer = setTimeout(() => failPlayerFollow(follow, 'player-follow:scene-timeout'), 180000);
    // A held, already-ready destination needs no second load event.
    if (checkReady) completeReady();
}
export function failPlayerFollow(follow: Follow, reason: string): void {
    clearTimeout(follow.timer);
    playerFollowReason(reason, follow.casterId, follow.destination);
    if (follow.destination) {
        const retry = follow.retry;
        follow.retry = () => {
            const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
            if (String(window.ftGetActiveEncounterId?.() ?? '').toUpperCase() !== follow.destination.toUpperCase() || !frame) { retry(); return; }
            // Retry the failed destination scene host in this document, retaining the follow notice.
            frame.src = frame.src;
            playerFollowLoading(follow, follow.destination, false);
        };
    }
    state(follow, 'failed', reason.startsWith('player-follow:catchup-')
        ? `Could not check your character’s room (${reason}).`
        : `Your character was moved, but the scene could not finish loading (${reason}).`, 'Retry');
}
export function offerPlayerFollow(casterId: string, destination: string, retry: () => void): void {
    const follow = beginPlayerFollow(casterId, destination, retry);
    window.dispatchEvent(new CustomEvent('ft:player-follow-offered', {
        detail: { reason: 'player-follow:other-room', actor_id: casterId, encounter_id: destination },
    }));
    state(follow, 'offered', `The GM moved your character${destinationName(destination) ? ` to ${destinationName(destination)}` : ''}. You are viewing another room.`, 'Go there');
}
